<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import atendimentoAPI from 'dashboard/api/atendimento';
import reportsAPI from 'dashboard/api/reports';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import FilterBar from 'dashboard/components-next/filter-bar/FilterBar.vue';
import PeriodPicker from 'dashboard/components-next/filter-bar/PeriodPicker.vue';
import { useFilterQuery } from 'dashboard/components-next/filter-bar/useFilterQuery';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ReportHeader from './components/ReportHeader.vue';
import ForaDaConta from './components/atendimento/ForaDaConta.vue';
import AgoraSection from './components/atendimento/AgoraSection.vue';
import NumerosSection from './components/atendimento/NumerosSection.vue';
import CaminhoSection from './components/atendimento/CaminhoSection.vue';
import IaSection from './components/atendimento/IaSection.vue';
import CanaisSection from './components/atendimento/CanaisSection.vue';
import CarrinhosTab from './components/atendimento/CarrinhosTab.vue';
import CampanhasTab from './components/campanhas/CampanhasTab.vue';
import TeamAfterAi from './components/analytics/TeamAfterAi.vue';
import { dayLabel } from './components/analytics/analyticsHelpers';
import { useAtendimentoSection } from './components/atendimento/useAtendimentoSection';
import {
  ATENDIMENTO_SCHEMA,
  apiFilters,
} from './components/atendimento/atendimentoFilters';
import {
  resolveTab,
  tabQuery,
  visibleTabs,
} from './components/atendimento/reportTabs';

// Relatórios (REPORTS-03): uma página, em abas, e cada aba responde uma
// pergunta — quem está com a conversa agora, os números do período
// (REPORTS-04), o caminho que ela fez no período, como a IA se saiu, o que a
// equipe fez depois da passagem, e a comparação por canal e time. O filtro e a
// aba moram na URL (`?tab=`, Agora fora dela); só a aba aberta pede, cada seção
// pede a própria rota, e a falha de uma não apaga as outras. Sem `KeepAlive` e sem rota filha por aba: a aba escondida não guarda
// timer, e o filtro é lido num lugar só. A barra de filtros é uma só, no
// cabeçalho de todas as abas (fase H).
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountId, currentAccount } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// O filtro escreve só as chaves dele (`ATENDIMENTO_SCHEMA`), sem gravar no
// histórico: a aba e o que cada aba guarda na URL ficam.
const { filters, update } = useFilterQuery(ATENDIMENTO_SCHEMA);
const requestFilters = computed(() => apiFilters(filters.value));

// O "comparar por" de "Canais e times" também mora na URL (`comparar`): é por
// ele que os links das abas de antes (Atendentes, Caixas, Times) abrem a tabela
// certa. Caixa é o padrão e não vai na URL.
const COMPARE_BY = ['inbox', 'team', 'user', 'agent'];
const compareBy = computed({
  get: () =>
    COMPARE_BY.includes(route.query.comparar) ? route.query.comparar : 'inbox',
  set: by =>
    router.replace({
      query: { ...route.query, comparar: by === 'inbox' ? undefined : by },
    }),
});

// A origem e o "Abrir por" do Caminho também moram na URL (`origem` e `abrir`,
// no molde do `comparar`), e só vão às rotas do Caminho (`caminho` e
// `ferramentas`). Sem eles, a árvore é a de todas as conversas, sem abrir.
const ORIGINS = ['client', 'outbound'];
const origin = computed({
  get: () => (ORIGINS.includes(route.query.origem) ? route.query.origem : null),
  set: value =>
    router.replace({ query: { ...route.query, origem: value || undefined } }),
});
const OPEN_BY = ['agent', 'inbox', 'team', 'user'];
const openBy = computed({
  get: () => (OPEN_BY.includes(route.query.abrir) ? route.query.abrir : null),
  set: value =>
    router.replace({ query: { ...route.query, abrir: value || undefined } }),
});

// O agrupamento da Números também mora na URL (`agrupar`), e só vai à rota
// dela: os mapas de calor não dependem dele. Dia é o padrão e não vai na URL.
const GRAINS = ['dia', 'semana', 'mes'];
const grain = computed({
  get: () =>
    GRAINS.includes(route.query.agrupar) ? route.query.agrupar : 'dia',
  set: value =>
    router.replace({
      query: { ...route.query, agrupar: value === 'dia' ? undefined : value },
    }),
});

// A aba depende da conta (Carrinhos e Campanhas têm flag): até a busca dela
// terminar, nada monta e nada pede — `?tab=carrinhos` pediria a Agora. O `client`
// vê relatório e não lê a conta (`AccountPolicy#show?`): a busca termina sem
// registro, e a página abre só com as abas sem flag.
//
// Limitação conhecida: por isso o `client` nunca vê Campanhas, embora a SPEC
// (§2.4, "quem vê: quem vê relatórios") e a API (200 para ele, R3-53) o
// incluam. Ler a flag de outra fonte pede o servidor mandá-la fora da conta.
// Em 29/09/2026, as contas com `outbounds` (1 e 4) não têm nenhum `client`.
const accountUIFlags = useMapGetter('accounts/getUIFlags');
const accountReady = computed(
  () =>
    Boolean(currentAccount.value?.id) || !accountUIFlags.value.isFetchingItem
);
const hasFlag = flag =>
  flag.account
    ? Boolean(currentAccount.value?.[flag.account])
    : isFeatureEnabledonAccount.value(accountId.value, flag.feature);
const tabs = computed(() => visibleTabs(hasFlag));
const activeTab = computed(() => resolveTab(route.query.tab, hasFlag));
const tabBarTabs = computed(() =>
  tabs.value.map(tab => ({ key: tab.key, label: t(tab.label) }))
);
const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeTab.value.key)
);

// A barra aparece desde o começo, com as pílulas da aba. Enquanto a conta
// carrega, a flag ainda é falsa e a aba resolvida é Agora: em `?tab=campanhas`,
// Time e Atendente piscariam ativos até a conta chegar. Até lá, vale a aba que o
// endereço pede.
const barTab = computed(() =>
  accountReady.value ? activeTab.value : resolveTab(route.query.tab, () => true)
);

// Trocar de aba grava no histórico (G11c), e leva o filtro junto.
const setTab = tab =>
  router.push({ query: { ...route.query, tab: tabQuery(tab.key) } });

const sections = {
  agora: useAtendimentoSection(f => atendimentoAPI.agora(f)),
  numeros: useAtendimentoSection((f, agrupar) =>
    atendimentoAPI.numeros(f, agrupar)
  ),
  mapa: useAtendimentoSection(f => atendimentoAPI.mapa(f)),
  caminho: useAtendimentoSection((f, options) =>
    atendimentoAPI.caminho(f, options)
  ),
  ia: useAtendimentoSection(f => atendimentoAPI.ia(f)),
  // As chamadas de ferramenta do runtime: rota do Caminho (a tarefa concluída,
  // que segue a origem dele) que "A IA" também lê, só pelas que falharam — e
  // essa lista não depende da origem. Um pedido só, à parte, sempre com a
  // origem: trocar de aba não pergunta ao runtime de novo.
  ferramentas: useAtendimentoSection((f, options) =>
    atendimentoAPI.ferramentas(f, options)
  ),
  equipe: useAtendimentoSection(f => atendimentoAPI.equipe(f)),
  comparar: useAtendimentoSection((f, by) => atendimentoAPI.comparar(f, by)),
  fora: useAtendimentoSection(f => atendimentoAPI.fora(f)),
  // Carrinhos pede o relatório v2, com as datas em segundos: no período do
  // filtro comum, por dia, e com "Incluir testes" (G10).
  carrinhos: useAtendimentoSection(f =>
    reportsAPI.getReports({
      metric: 'cart_links_count',
      from: f.period.from,
      to: f.period.to,
      groupBy: 'day',
      includeTests: Boolean(f.includeTests),
    })
  ),
};
const {
  agora,
  numeros,
  mapa,
  caminho,
  ia,
  ferramentas,
  equipe,
  comparar,
  fora,
  carrinhos,
} = sections;

// Pela chave, e não pelo objeto: a mesma chave não pede de novo, e trocar só o
// "comparar por" (ou a origem, ou o "Abrir por") não pede as outras seções.
const filterKey = computed(() => JSON.stringify(requestFilters.value));
const originKey = computed(() => `${filterKey.value}|${origin.value || ''}`);
// Carrinhos só pede pelo período e pelo teste (G10): um filtro apagado nela,
// trocado noutra aba, não a pede de novo.
const cartKey = computed(() => {
  const { since, until, includeTests } = requestFilters.value;
  return JSON.stringify({ since, until, includeTests });
});

const loadTab = () => {
  if (!accountReady.value) return;
  const f = requestFilters.value;
  activeTab.value.sections.forEach(name => {
    if (name === 'comparar') {
      comparar.loadIfStale(
        `${filterKey.value}|${compareBy.value}`,
        f,
        compareBy.value
      );
    } else if (name === 'numeros') {
      numeros.loadIfStale(`${filterKey.value}|${grain.value}`, f, grain.value);
    } else if (name === 'carrinhos') {
      carrinhos.loadIfStale(cartKey.value, filters.value);
    } else if (name === 'caminho') {
      caminho.loadIfStale(`${originKey.value}|${openBy.value || ''}`, f, {
        origin: origin.value || undefined,
        by: openBy.value || undefined,
      });
    } else if (name === 'ferramentas') {
      ferramentas.loadIfStale(originKey.value, f, {
        origin: origin.value || undefined,
      });
    } else {
      sections[name].loadIfStale(filterKey.value, f);
    }
  });
};

// A hora da última resposta da Agora. A Equipe a lê uma vez, sem se refazer, e
// diz de quando ela é: "às HH:MM" (G11b).
const agoraAt = ref(null);
watch(
  () => agora.data.value,
  data => {
    agoraAt.value = data ? new Date() : null;
  }
);

// A Equipe reaproveita a Agora da tela só se ela é do último minuto, o
// intervalo em que a própria Agora se refaz. Mais velha que isso, a Equipe lê
// de novo: "às 10:00" seria verdade, mas o dado teria uma hora (D2).
const AGORA_MAX_AGE = 60000;
const agoraIsOld = () =>
  Boolean(agoraAt.value) &&
  Date.now() - agoraAt.value.getTime() > AGORA_MAX_AGE;

// "Agora" é o estado do minuto: ele se refaz sozinho, e só com a aba aberta; o
// resto é do período.
const { startRefetching, stopRefetching } = useLiveRefresh(() =>
  agora.load(requestFilters.value)
);

watch(
  () => (accountReady.value ? activeTab.value.key : null),
  (key, previous) => {
    if (!key) return;
    const tab = activeTab.value;
    if (tab.live) {
      // Voltar à Agora recarrega na hora: o que ela mostrava já envelheceu.
      if (previous) tab.sections.forEach(name => sections[name].invalidate());
      startRefetching();
    } else {
      stopRefetching();
      if (tab.sections.includes('agora') && agoraIsOld()) agora.invalidate();
    }
    loadTab();
  },
  { immediate: true }
);
watch([filterKey, compareBy, origin, openBy, grain], loadTab);

onMounted(() => {
  store.dispatch('agents/get');
});

// Os agentes de IA do filtro vêm em toda resposta (e nas opções de Campanhas) e
// se acumulam: escolher um agente não pode sumir com os outros da lista.
const aiAgents = ref([]);
const addAgents = agents => {
  const byId = new Map(aiAgents.value.map(agent => [agent.id, agent]));
  (agents || []).forEach(agent => byId.set(agent.id, agent));
  aiAgents.value = [...byId.values()];
};
Object.values(sections).forEach(section =>
  watch(() => section.data.value?.agents, addAgents)
);

// As opções das pílulas que são de uma aba só (as de Campanhas), que a própria
// aba pede e manda para cá.
const tabOptions = ref({});
const setTabOptions = ({ agents, ...options }) => {
  addAgents(agents);
  tabOptions.value = options;
};

const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');
const users = useMapGetter('agents/getAgents');

const asOptions = (list, labelOf = item => item.name) =>
  (list || []).map(item => ({ id: item.id, name: labelOf(item) }));
const FILTER_LABELS = {
  agentId: 'ATENDIMENTO_REPORTS.FILTERS.AI_AGENT',
  inboxId: 'ATENDIMENTO_REPORTS.FILTERS.INBOX',
  teamId: 'ATENDIMENTO_REPORTS.FILTERS.TEAM',
  userId: 'ATENDIMENTO_REPORTS.FILTERS.ATTENDANT',
  campanha: 'CAMPANHAS.FILTERS.CAMPAIGN',
  template: 'CAMPANHAS.FILTERS.TEMPLATE',
  segmento: 'CAMPANHAS.FILTERS.SEGMENT',
};

// As pílulas da barra. As comuns são `single` e `scalar`: a API recebe um id
// só. As que a aba não aplica ficam apagadas, com o porquê, e as que são só de
// uma aba (múltiplas) aparecem só nela.
const fields = computed(() => {
  const tab = barTab.value;
  const common = [
    { key: 'agentId', options: asOptions(aiAgents.value) },
    { key: 'inboxId', options: asOptions(inboxes.value) },
    { key: 'teamId', options: asOptions(teams.value) },
    {
      key: 'userId',
      options: asOptions(users.value, user => user.available_name || user.name),
    },
  ].map(field => ({ ...field, single: true, scalar: true }));
  const own = (tab.ownFilters || []).map(key => ({
    key,
    options: tabOptions.value[key] || [],
  }));
  return [...common, ...own].map(field => ({
    ...field,
    label: t(FILTER_LABELS[field.key]),
    ...((tab.disabledFilters || []).includes(field.key)
      ? { disabled: true, disabledHint: t(tab.disabledHint) }
      : {}),
  }));
});

// "Limpar filtros" limpa as pílulas que valem na aba. O período e o teste ficam
// (não são pílula), e as apagadas também: são o filtro das outras abas.
const clearFilters = () =>
  update(
    Object.fromEntries(
      fields.value
        .filter(field => !field.disabled)
        .map(field => [field.key, field.scalar ? undefined : []])
    )
  );

const filterPeriod = computed({
  get: () => filters.value.period,
  set: value => update({ period: value }),
});
const includeTests = computed({
  get: () => Boolean(filters.value.includeTests),
  set: value => update({ includeTests: value }),
});

// O período de qualquer seção carregada, a do Caminho primeiro. Serve ao
// Caminho; a Números escreve o dela, da própria resposta.
const rangeLabel = computed(() => {
  const period = [caminho, ia, ferramentas, equipe, comparar]
    .map(section => section.data.value?.period)
    .find(Boolean);
  return period
    ? t('ATENDIMENTO_REPORTS.FILTERS.RANGE', {
        since: dayLabel(period.since),
        until: dayLabel(period.until),
      })
    : '';
});

const partialFilters = computed(() =>
  Boolean(filters.value.teamId || filters.value.userId)
);
</script>

<template>
  <ReportHeader
    :header-title="t('ATENDIMENTO_REPORTS.HEADER')"
    :header-description="t('ATENDIMENTO_REPORTS.DESCRIPTION')"
  >
    <span
      v-if="accountReady && activeTab.live"
      class="inline-flex gap-1.5 items-center px-2 py-1 text-xs font-medium whitespace-nowrap rounded-full bg-n-teal-3 text-n-teal-11"
    >
      <span class="rounded-full size-1.5 bg-n-teal-9" />
      <span class="sm:hidden">{{ t('ATENDIMENTO_REPORTS.LIVE_SHORT') }}</span>
      <span class="hidden sm:inline">{{ t('ATENDIMENTO_REPORTS.LIVE') }}</span>
    </span>
  </ReportHeader>

  <div
    class="flex flex-col gap-4 pb-6 w-full min-w-0"
    data-test-id="atendimento"
  >
    <FilterBar
      data-test-id="atendimento-filtros"
      :model-value="filters"
      :fields="fields"
      :search="false"
      @update:model-value="update"
      @reset="clearFilters"
    >
      <template #period>
        <PeriodPicker v-model="filterPeriod" />
      </template>
      <template #toggles>
        <label
          class="flex gap-2 items-center m-0 text-sm font-normal leading-normal cursor-pointer text-n-slate-11"
        >
          <Switch
            v-model="includeTests"
            data-test-id="atendimento-filtro-teste"
          />
          {{ t('ATENDIMENTO_REPORTS.FILTERS.INCLUDE_TESTS') }}
        </label>
      </template>
    </FilterBar>
    <template v-if="accountReady">
      <!-- 8 abas passam de 390 px: rola só a faixa, e não a página. -->
      <div class="overflow-x-auto max-w-full" data-test-id="atendimento-tabs">
        <TabBar
          :tabs="tabBarTabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="setTab"
        />
      </div>
      <AgoraSection
        v-if="activeTab.key === 'agora'"
        :report="agora.data.value"
        :failed="agora.failed.value"
        :filters="requestFilters"
      />
      <NumerosSection
        v-else-if="activeTab.key === 'numeros'"
        v-model:grain="grain"
        :report="numeros.data.value"
        :failed="numeros.failed.value"
        :mapa="mapa.data.value"
        :mapa-failed="mapa.failed.value"
      />
      <template v-else-if="activeTab.key === 'caminho'">
        <ForaDaConta
          v-if="fora.data.value || fora.failed.value"
          :report="fora.data.value"
          :failed="fora.failed.value"
        />
        <CaminhoSection
          v-model:origin="origin"
          v-model:by="openBy"
          :report="caminho.data.value"
          :failed="caminho.failed.value"
          :tasks="ferramentas.data.value"
          :tasks-failed="ferramentas.failed.value"
          :filters="requestFilters"
          :range-label="rangeLabel"
        />
      </template>
      <IaSection
        v-else-if="activeTab.key === 'ia'"
        :report="ia.data.value"
        :failed="ia.failed.value"
        :tools="ferramentas.data.value"
        :tools-failed="ferramentas.failed.value"
        :partial-filters="partialFilters"
      />
      <TeamAfterAi
        v-else-if="activeTab.key === 'equipe'"
        :report="equipe.data.value"
        :failed="equipe.failed.value"
        :now="agora.data.value"
        :now-at="agoraAt"
        :now-failed="agora.failed.value"
      />
      <CanaisSection
        v-else-if="activeTab.key === 'canais'"
        v-model:by="compareBy"
        :report="comparar.data.value"
        :failed="comparar.failed.value"
      />
      <CampanhasTab
        v-else-if="activeTab.key === 'campanhas'"
        @options="setTabOptions"
      />
      <CarrinhosTab
        v-else-if="activeTab.key === 'carrinhos'"
        :report="carrinhos.data.value"
        :loading="carrinhos.loading.value"
        :failed="carrinhos.failed.value"
        :error="carrinhos.error.value"
      />
    </template>
  </div>
</template>
